import React, {useEffect, useRef, useState} from "react";
import ListAlt from "@mui/icons-material/ListAlt";
import CalendarMonth from "@mui/icons-material/CalendarMonth";
import SortIcon from "@mui/icons-material/Sort";
import ChevronRight from "@mui/icons-material/ChevronRight";
import styles from "../style/components/RecFilterModal.module.scss";
import ExploreIcon from "@mui/icons-material/Explore";
import {useNavigate} from "react-router-dom";
import * as stringFormatting from "../utils/StringFormatting";
import {CitySortBy} from "../backend/apiService";
import Tooltip from '@mui/material/Tooltip';

type Props = {
    currentCityId: string;
    currentCityTitle: string;
    showPlaylist: boolean;
    playlistLink: string | undefined;
    eventDateRange: string | undefined;
    onDateClick: (() => void) | undefined;
    sortBy: CitySortBy | undefined;
    sortOptionClicked: ((option: CitySortBy) => void) | undefined;
}

const RecFilterModal: React.FC<Props> = ({
                                             currentCityId,
                                             currentCityTitle,
                                             showPlaylist,
                                             playlistLink,
                                             eventDateRange,
                                             onDateClick,
                                             sortBy,
                                             sortOptionClicked
                                         }) => {
    const navigate = useNavigate();

    // State to track whether the “Sort By” dropdown is open
    const [isSortOpen, setIsSortOpen] = useState(false);

    // Ref used to detect clicks outside the dropdown
    const sortRef = useRef<HTMLDivElement>(null);

    // Close dropdown if user clicks anywhere outside of sortRef
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                sortRef.current &&
                !sortRef.current.contains(event.target as Node)
            ) {
                setIsSortOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <div className={styles["modal-overlay"]}>
            <div className={styles["modal"]}>
                <div className={styles["content"]}>
                    <h1>Options</h1>
                </div>

                {/* ---- OPTION CARDS LIST ---- */}
                <div className={styles["options-list"]}>
                    {/* About City */}
                    <div
                        className={styles["option-card"]}
                        onClick={() => navigate(`/city/${currentCityId}/${stringFormatting.toSlug(currentCityTitle)}`)}
                    >
                        <div className={styles["option-icon"]}>
                            <ExploreIcon/>
                        </div>
                        <div className={styles["option-title"]}>About {currentCityTitle}</div>
                        <div className={styles["option-chevron"]}>
                            <ChevronRight/>
                        </div>
                    </div>

                    {/* Personalized Playlist */
                        showPlaylist && (
                            playlistLink ? (
                                <div
                                    className={styles["option-card"]}
                                    onClick={() => window.open(`https://open.spotify.com/playlist/${playlistLink}`, "_blank")}
                                >
                                    <div className={styles["option-icon"]}>
                                        <ListAlt/>
                                    </div>
                                    <div className={styles["option-title"]}>Personalized Playlist</div>
                                    <div className={styles["option-chevron"]}>
                                        <ChevronRight/>
                                    </div>
                                </div>
                            ) : (
                                <Tooltip title={"Playlist Unavailable"} placement="bottom" arrow>
                                    <div
                                        className={`${styles["option-card"]} ${styles["disabled"]}`}
                                    >
                                        <div className={styles["option-icon"]}>
                                            <ListAlt/>
                                        </div>
                                        <div className={styles["option-title"]}>Personalized Playlist</div>
                                        <div className={styles["option-chevron"]}>
                                            <ChevronRight/>
                                        </div>
                                    </div>
                                </Tooltip>
                            )
                        )
                    }

                    {/* Event Date Range */
                        eventDateRange && (
                            <div
                                className={styles["option-card"]}
                                onClick={onDateClick}
                            >
                                <div className={styles["option-icon"]}>
                                    <CalendarMonth/>
                                </div>
                                <div className={styles["option-title"]}>{eventDateRange}</div>
                                <div className={styles["option-chevron"]}>
                                    <ChevronRight/>
                                </div>
                            </div>
                        )
                    }

                    {/* Sort By (with dropdown) */
                        sortBy && (
                            <div
                                className={styles["option-card"]}
                                ref={sortRef}
                                onClick={() => setIsSortOpen((prev) => !prev)}
                            >
                                <div className={styles["option-icon"]}>
                                    <SortIcon/>
                                </div>
                                <div className={styles["option-title"]}>
                                    Sort Events By: {sortBy === CitySortBy.SCORE ? "Percent Match" : "Date"}
                                </div>
                                <div className={styles["option-chevron"]}>
                                    <ChevronRight/>
                                </div>

                                {isSortOpen && (
                                    <div className={styles["dropdown-menu"]}>
                                        <div
                                            className={styles["dropdown-item"]}
                                            onClick={() => {
                                                if (sortBy !== CitySortBy.SCORE && sortOptionClicked)
                                                    sortOptionClicked(CitySortBy.SCORE);
                                            }}
                                        >
                                            Percent Match
                                        </div>
                                        <div
                                            className={styles["dropdown-item"]}
                                            onClick={() => {
                                                if (sortBy !== CitySortBy.DATE && sortOptionClicked)
                                                    sortOptionClicked(CitySortBy.DATE);
                                            }}
                                        >
                                            Date
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                </div>
            </div>
        </div>
    );
};

export default RecFilterModal;
